/**
 * UsageMetersPanel — Metered usage display (Rule 33)
 *
 * Presentational component showing usage across 5 dimensions:
 * Integrations, Records, AI Operations, Storage, Workflows.
 *
 * Parent provides the workspace usage snapshot (`GET /v1/workspace/usage`): each count with
 * the limit the server resolved for this tenant. This component only renders.
 *
 * Counts and limits both come from that snapshot (2026-10-08). The panel used to pair one daily
 * `usage_meters` row with built-in default limits (10 integrations, 10 workflows, …), and it
 * drew the day's sync-operation count as both Integrations and Workflows. An unlimited tenant
 * with 9 connections read "8.0K / 10 · at limit".
 */

import { memo, useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../primitives/card.js';
import { UsageMeterBar } from './UsageMeterBar.js';
import { formatCompactNumber } from '../utils.js';
import { BarChart3, RefreshCcw, AlertTriangle } from 'lucide-react';

/**
 * The fields of `GET /v1/workspace/usage` the panel reads. A limit of -1 is unlimited. Postgres
 * bigints can arrive as numeric strings, so each value is read through `Number()`.
 */
export interface WorkspaceUsageSnapshot {
  readonly integrations: number | string;
  readonly integrations_limit: number | string;
  readonly records: number | string;
  readonly records_limit: number | string;
  readonly ai_operations: number | string;
  readonly ai_operations_limit: number | string;
  readonly storage_gb: number | string;
  readonly storage_limit_gb: number | string;
  readonly workflows: number | string;
  readonly workflows_limit: number | string;
}

export interface UsageMetersPanelProps {
  /** The workspace usage snapshot; nothing renders until it arrives. */
  readonly usage: WorkspaceUsageSnapshot | null;
  /** Last fetch timestamp for display; updated by parent polling */
  readonly lastFetchTime?: Date | null;
  /** Called when a meter is clicked (e.g., navigate to billing) */
  readonly onMeterClick?: () => void;
}

const LimitOptions = memo(function LimitOptions({
  dimension,
  onNavigateBilling,
}: {
  readonly dimension: string;
  readonly onNavigateBilling: () => void;
}) {
  return (
    <div className="mt-2 p-3 rounded-md border border-destructive-bg bg-destructive-bg">
      <div className="flex items-center gap-1.5 mb-2">
        <AlertTriangle className="w-3.5 h-3.5 text-destructive" />
        <span className="text-xs font-medium text-destructive">
          {dimension} limit reached
        </span>
      </div>
      <button
        type="button"
        onClick={onNavigateBilling}
        className="flex items-center justify-between w-full px-2.5 py-1.5 rounded text-xs bg-card border border-border hover:bg-(--content-background) transition-colors"
      >
        <span className="text-(--text-primary) font-medium">Upgrade or add capacity</span>
      </button>
    </div>
  );
});

function num(value: number | string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** -1 is unlimited and 0 means no limit was resolved: neither has a ceiling to draw against. */
function limitText(limit: number, format: (n: number) => string): string {
  if (limit < 0) return 'Unlimited';
  if (limit === 0) return '—';
  return format(limit);
}

export function UsageMetersPanel({ usage, lastFetchTime, onMeterClick }: UsageMetersPanelProps) {
  const meters = useMemo(() => {
    if (!usage) return [];
    const count = (current: number | string, limit: number | string, key: string, label: string) => {
      const c = num(current), l = num(limit);
      return { key, label, current: c, limit: l, currentFormatted: formatCompactNumber(c), limitFormatted: limitText(l, formatCompactNumber) };
    };
    const storage = num(usage.storage_gb), storageLimit = num(usage.storage_limit_gb);
    return [
      count(usage.integrations, usage.integrations_limit, 'integrations', 'Integrations'),
      count(usage.records, usage.records_limit, 'records', 'Records'),
      count(usage.ai_operations, usage.ai_operations_limit, 'ai-operations', 'AI Operations'),
      {
        key: 'storage',
        label: 'Storage',
        current: storage,
        limit: storageLimit,
        currentFormatted: `${storage.toFixed(2)} GB`,
        limitFormatted: limitText(storageLimit, (n) => `${n} GB`),
      },
      count(usage.workflows, usage.workflows_limit, 'workflows', 'Workflows'),
    ];
  }, [usage]);

  if (!usage) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Usage Meters
          </CardTitle>
          {lastFetchTime && (
            <div className="flex items-center gap-2 text-3xs text-muted-foreground">
              <RefreshCcw className="w-3 h-3" />
              Last updated: {lastFetchTime.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              })}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {meters.map((meter) => {
            const isExceeded = meter.limit > 0 && meter.current >= meter.limit;

            return (
              <div key={meter.key}>
                <UsageMeterBar
                  label={meter.label}
                  current={meter.current}
                  limit={meter.limit}
                  currentFormatted={meter.currentFormatted}
                  limitFormatted={meter.limitFormatted}
                  {...(onMeterClick ? { onClick: onMeterClick } : {})}
                />
                {isExceeded && (
                  <LimitOptions
                    dimension={meter.label}
                    onNavigateBilling={onMeterClick ?? (() => {})}
                  />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
